import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  formatDateAnswer,
  TRAVEL_CLAIM_TYPE_LABELS,
  type CaseAnswers,
  type FlowStep,
} from '@tci/shared-types';

import { Button } from '@/components/ui/button';
import { useStrayDropGuard } from '@/hooks/use-stray-drop-guard';
import { isRateLimited } from '@/lib/http-errors';
import { cn } from '@/lib/utils';
import {
  clearFormSession,
  hasFormSession,
  isFormChannelSession,
  removeFormDocument,
  uploadFormDocument,
  useFormState,
  useRefreshFormState,
  useSendFormTurn,
  useStartFormConversation,
  type FormState,
} from '@/hooks/use-form-conversation';
import { FieldControl } from './field-control';
import { FormShell, PreClaimLayout, SectionLayout, SHOW_CHAT_ALTERNATIVE } from './layout';
import { CodeBoxes, RESEND_SECONDS } from './code-entry';
import { CheckIcon } from './icons';
import { keepDigits } from './digits-only';
import { checkMobileNumber, toE164 } from './mobile-number';
import { copyFor } from './form-copy';
import { ReviewStage, type ReviewRow } from './review';
import { focusField } from './focus-field';
import {
  backTargetFor,
  rowClassFor,
  rowsFor,
  sectionsFor,
  SECTIONS,
  type ResolvedSection,
} from './sections';
import { missingRequired, submitSection, type TurnOutcome } from './submit-engine';

/**
 * The claim form — a fourth way to lodge a claim, beside the chat, WhatsApp and
 * Telegram.
 *
 * It is the same conversation engine underneath: the server holds the
 * questions, decides what comes next and stores the answers. What differs is
 * the shape. The chat asks one thing at a time; this shows a section at once
 * and sends it field by field, in the order the server expects.
 *
 * Which screen to draw is the *server's* answer, read from `/state`. Working it
 * out here would mean the form owning a second description of where a claim has
 * got to, and the two would drift the first time a flow changed.
 */

const newTurnId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const browserLocale = (): string | undefined => navigator.language?.split('-')[0];

/**
 * The claim-type question is asked before any flow exists, so it has no step in
 * `flow.steps` — but it is still the form's first section, and its wording
 * belongs beside the other five rather than inline here where the two would
 * drift.
 */
const CLAIM_TYPE_SECTION = SECTIONS.find(section => section.id === 'claim-type')!;

export function ClaimFormPage() {
  const { data: state, isLoading } = useFormState();
  const start = useStartFormConversation();
  const refresh = useRefreshFormState();
  const send = useSendFormTurn();

  // Changing language is an ordinary turn carrying the new locale, not a
  // client-side toggle: the questions and every reply are the server's, so it
  // is the server that has to be told. §1.3 makes every turn carry it.
  const setLocale = async (locale: 'en' | 'ms') => {
    await send.mutateAsync({ clientMessageId: newTurnId(), locale, text: '' });
    await refresh();
  };

  // Opened once, and gated on the *session* rather than on `state`.
  //
  // `/state` answers `stage: 'phone'` for a browser holding no session at all,
  // which is the right screen to draw but the wrong thing to reason from: a
  // truthy state is not evidence that a conversation exists. Gating on it meant
  // `start` never ran, no session was minted, and every turn went to nobody —
  // the form sat on the first screen with a 201 for each answer.
  const started = useRef(false);
  useEffect(() => {
    if (started.current || hasFormSession()) return;
    started.current = true;
    start.mutate(browserLocale());
  }, [start]);

  useEffect(() => {
    if (start.isSuccess) void refresh();
  }, [start.isSuccess, refresh]);

  if (isLoading || !state) {
    return (
      <FormShell>
        <div className="flex flex-1 items-center justify-center p-10 text-sm text-muted-foreground">
          Loading…
        </div>
      </FormShell>
    );
  }

  return (
    <FormShell reference={state.case?.caseNumber} locale={state.locale} onLocaleChange={setLocale}>
      <StageRouter state={state} />
    </FormShell>
  );
}

function StageRouter({ state }: { state: FormState }) {
  switch (state.stage) {
    case 'phone':
      return <PhoneStage state={state} />;
    case 'code':
      return <CodeStage state={state} />;
    case 'consent':
      return <ConsentStage state={state} />;
    case 'claim-type':
      return <ClaimTypeStage state={state} />;
    case 'submitted':
      return <SubmittedStage state={state} />;
    case 'flow':
    default:
      return <FlowStage state={state} />;
  }
}

/** Send one turn and re-read the state. Used by every pre-claim screen. */
/**
 * How the conversation marks a question: `(4 of 16) And when does your trip…`.
 *
 * Only questions are numbered, which makes this the way to tell a re-ask from
 * the reason for it — see `deps.send`, where a refusal has to be reported under
 * the field that caused it.
 */
const NUMBERED_QUESTION = /^\(\d+ of \d+\)/;

function useSimpleTurn() {
  const send = useSendFormTurn();
  const refresh = useRefreshFormState();
  /*
    Busy until the screen has caught up, not until the request has.

    `send.isPending` goes false the moment the server answers — which is *before*
    the re-read that tells this page what the answer was. In that gap the screen
    holds the previous state while presenting itself as idle, and anything
    rendered from `lastReply` is the reply to the last turn shown as the reply to
    this one: press Send on a perfectly good number and the bot's greeting
    appears in red for a second, reading exactly like a validation failure, until
    the real state lands and the page moves on.

    So the turn is not over until the state behind it is.
  */
  const [settling, setSettling] = useState(false);

  return {
    busy: send.isPending || settling,
    submit: async (turn: { text?: string; callbackValue?: string }) => {
      setSettling(true);
      try {
        await send.mutateAsync({
          clientMessageId: newTurnId(),
          locale: browserLocale(),
          ...turn,
        });
        await refresh();
      } finally {
        setSettling(false);
      }
    },
  };
}

const READY = [
  'Your policy number',
  'Passport or IC',
  'Boarding pass or itinerary',
  'Airline letter, police report or receipts for what happened',
  'Bank account for the payout',
];

function PhoneStage({
  state,
  onSent,
}: {
  state: FormState;
  /**
   * The number this screen has just sent, if anyone is waiting on it.
   *
   * Only the code screen passes one: it renders this same screen for **Back**,
   * and needs to know which number to watch for coming back as the pending one.
   * On the real phone stage nobody listens, because the server moving to `code`
   * is the answer.
   */
  onSent?: (phone: string) => void;
}) {
  const [phone, setPhone] = useState('');
  const [attempted, setAttempted] = useState(false);
  /** What this screen found wrong with the number, before anything was sent. */
  const [invalid, setInvalid] = useState<string | null>(null);
  const { busy, submit } = useSimpleTurn();
  const t = copyFor(state.locale);

  /*
    Checked here, then sent. E.164 for the server, "+60" shown as a prefix for
    the claimant — nobody types a country code into a form on their own phone.

    The check is not the server's job done twice. The server's refusal costs a
    round trip and lands after a wait, by which time the screen has said a code
    is on its way; a number one digit short is something this field can see for
    itself, immediately, next to the box it is about.
  */
  const send = () => {
    const wrong = checkMobileNumber(phone);
    if (wrong) {
      setInvalid(wrong);
      document.getElementById('phone')?.focus();
      return;
    }
    setAttempted(true);
    void submit({ text: toE164(phone) });
    onSent?.(toE164(phone));
  };

  return (
    <div className="flex flex-1 justify-center px-4 py-10 sm:px-16 sm:py-16">
      <div className="grid w-full max-w-[1180px] gap-10 lg:grid-cols-[1fr_380px]">
        <main className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-[0.08em] text-primary">
              Travel insurance claims
            </span>
            <h1 className="text-3xl font-bold leading-tight tracking-tight sm:text-[40px]">
              Make your travel claim
              <br className="hidden sm:block" /> online, in about ten minutes.
            </h1>
            <p className="max-w-[540px] text-[15px] leading-relaxed text-muted-foreground">
              Flight delays, lost or damaged luggage, cancelled trips and overseas medical bills.
              Six short steps, saved as you go.
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="phone" className="text-sm font-semibold">
              {t('mobileNumber')}
            </label>
            <div className="flex max-w-[540px] items-center gap-2 rounded-lg border border-input bg-background px-3.5">
              <span className="font-medium text-muted-foreground">+60</span>
              <input
                id="phone"
                type="tel"
                inputMode="tel"
                placeholder="12 345 6789"
                value={phone}
                /*
                  Digits only. A mobile number has no letters in it, and the
                  server strips everything else before sending anyway — so a
                  claimant who typed one could press Send, wait, and be told
                  nothing was wrong with a number they can see is wrong.
                  Spaces and dashes go the same way, because people type them:
                  "012-345 6789" is how the number is written down.
                */
                onChange={event => {
                  setPhone(keepDigits(event.target.value));
                  // Punish late, forgive early: the message stops being true
                  // the moment they start fixing it, and leaving it in red
                  // under a box they are correcting reads as "still wrong".
                  setInvalid(null);
                }}
                onKeyDown={event => event.key === 'Enter' && send()}
                aria-invalid={invalid ? true : undefined}
                aria-describedby={invalid ? 'phone-error' : undefined}
                className="w-full bg-transparent py-3 text-base focus:outline-none"
              />
            </div>
            {/*
              Said plainly, and never as "we text you". There is no SMS anywhere
              in the system, so somebody without WhatsApp cannot use this at all
              — finding that out after typing their number is worse than being
              told before.
            */}
            <p className="max-w-[540px] text-xs leading-snug text-muted-foreground">
              We send a 6-digit code to this number on <strong>WhatsApp</strong>. Started already?
              Enter the same number on this device and we pick up where you left off.
            </p>
            {/*
              `lastReply` is only an error once the claimant has tried
              something, **and** the answer to that attempt is in. On a
              conversation that has just opened it is the bot's *greeting* —
              "Hello, we handle insurance claims…" — and rendering that in red as
              a `role="alert"` told everyone arriving at the form that something
              had already gone wrong, before they had touched it. While a turn is
              in flight it is the reply to the *previous* one, which is worse: it
              appears at the moment of pressing Send and reads as a verdict on
              the number just typed. A message is a failure only if it is the
              reply to the attempt being made.
            */}
            {/*
              One line, whoever is speaking. This screen's own objection wins
              while it stands, because it is about the number now in the box —
              the server's is about the one that was sent, and showing both
              would have a claimant reading two verdicts on one field.
            */}
            {(invalid || (attempted && !busy && state.lastReply)) && (
              <p id="phone-error" role="alert" className="text-xs text-destructive">
                {invalid ?? state.lastReply}
              </p>
            )}
          </div>

          <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:flex-wrap sm:items-center">
            {/*
              Full width on a phone, as everywhere else in the form: it is the
              only action on the screen, and a pill hugging two words is a small
              target sitting in a lot of empty space.
            */}
            {/*
              Live even on an empty field. A greyed-out button says no without
              saying why, and on a screen whose hint runs three lines the
              claimant reads that as the site being broken. Pressing it names
              what is missing, which is the thing they can act on.
            */}
            <Button size="lg" className="w-full sm:w-auto" disabled={busy} onClick={send}>
              {busy ? t('sending') : t('sendCode')}
            </Button>
            {/*
              Hidden with the sidebar card, and for the same reason: both links
              go to "#". Offering two other ways in and landing the claimant
              back on this page is worse than offering none — and this one sits
              beside the button they are meant to press.
            */}
            {SHOW_CHAT_ALTERNATIVE && (
              <p className="text-sm text-muted-foreground">
                Or message us on{' '}
                <a href="#" className="text-primary underline">
                  WhatsApp
                </a>{' '}
                or{' '}
                <a href="#" className="text-primary underline">
                  Telegram
                </a>
                , with the same questions and the same team.
              </p>
            )}
          </div>
        </main>

        {/*
          Beside the form on a wide screen, below it on a phone. Static text,
          and the thing that stops people abandoning at Evidence: somebody who
          reaches the uploads without their boarding pass to hand leaves.
        */}
        <aside className="flex h-fit flex-col gap-2.5 rounded-xl border bg-background p-5">
          <h2 className="text-sm font-semibold">{t('haveTheseReady')}</h2>
          {READY.map(item => (
            <div key={item} className="flex items-start gap-2.5 text-sm">
              {/*
                A filled circle rather than a bare tick, as the design has it.
                A row of loose ✓ marks reads as five things already done; a
                marker reads as a list to check against, which is what this is.
              */}
              <span
                className="mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
                aria-hidden="true"
              >
                <CheckIcon className="h-3 w-3" />
              </span>
              {item}
            </div>
          ))}
          <p className="mt-1 text-xs leading-snug text-muted-foreground">
            Missing something? Start anyway. We save as you go, and you can come back on this device
            to add documents.
          </p>
        </aside>
      </div>
    </div>
  );
}

function CodeStage({ state }: { state: FormState }) {
  const [code, setCode] = useState('');
  const [remaining, setRemaining] = useState(RESEND_SECONDS);
  const [attempted, setAttempted] = useState(false);
  /*
    Back, meaning back.

    This screen used to answer **Back** by opening a panel titled "Send the code
    to a different number" — the same panel the "Wrong number?" link opened, so
    two controls did one thing and the one carrying the universal word was the
    one not doing the universal thing.

    There is no page to pop: the stage is the server's, and it is at `code`. But
    the way *back* is the way the number arrived, and the gateway takes a new
    number at this step and replaces the pending one. So Back re-renders the
    number screen, and sending from it is the same turn the panel sent. One
    screen fewer, one state fewer, and the word means what it says.
  */
  const [goingBack, setGoingBack] = useState(false);
  /** The number sent from that screen, until the server says it is the pending one. */
  const [awaiting, setAwaiting] = useState<string | null>(null);
  const { busy, submit } = useSimpleTurn();
  const t = copyFor(state.locale);

  /*
    Come back once the code is going to the number that was just typed.

    Read from `pendingPhone` rather than from the turn resolving, because those
    are different facts: a refused number resolves too, and bouncing off the
    screen would take its refusal with it — the claimant would land here with no
    idea why the number they gave was not taken. Re-entering the *same* number
    satisfies it immediately, which is right: a fresh code has been sent to it.
  */
  const pending = state.pendingPhone ? keepDigits(state.pendingPhone) : null;
  // Before the paint, not after: an ordinary effect leaves one frame in which
  // the number screen is on show with the *new* pending number's confirmation
  // under it — the code screen's message, on the wrong screen, in red.
  useLayoutEffect(() => {
    if (!awaiting || keepDigits(awaiting) !== pending) return;
    setAwaiting(null);
    setGoingBack(false);
    setCode('');
    setAttempted(false);
    setRemaining(RESEND_SECONDS);
  }, [awaiting, pending]);

  // Counts down once, from when this screen appeared. Not persisted: a reload
  // is a fair reason to be allowed another code, and the server's own per-number
  // limit is what actually protects the sending cost.
  useEffect(() => {
    if (remaining <= 0) return;
    const timer = setTimeout(() => setRemaining(seconds => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [remaining]);

  /**
   * Send the code, and be honest about a refusal.
   *
   * The screen said nothing when a code was wrong: the digits stayed in the
   * boxes, the button un-pressed itself, and that was all. Somebody who
   * mistyped one digit could not tell whether they had got it wrong, the
   * message had not arrived, or the site was broken — and the boxes were still
   * full, so trying again meant clearing six digits by hand first.
   *
   * The server's own words are shown rather than a message written here,
   * because it distinguishes a case this screen cannot see: the fifth wrong
   * code drops the pending number entirely, and the claimant has to send their
   * number again rather than keep guessing at a code that no longer exists.
   */
  const confirm = async () => {
    if (code.length !== 6) return;
    await submit({ text: code });
    setAttempted(true);
    setCode('');
  };

  if (goingBack) return <PhoneStage state={state} onSent={setAwaiting} />;

  return (
    <PreClaimLayout
      eyebrow="Before we start"
      title="Check your messages"
      actions={
        <>
          <Button variant="outline" disabled={busy} onClick={() => setGoingBack(true)}>
            {t('back')}
          </Button>
          <Button disabled={busy || code.length < 6} onClick={confirm}>
            {busy ? t('checking') : 'Confirm'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label htmlFor="code" className="text-sm font-semibold">
          Enter the 6-digit code
        </label>

        {/*
          No auto-submit on the sixth digit, deliberately, even though it is the
          common pattern. A claimant gets five attempts before the server sends
          them back to the number step, so a mistyped digit that submits itself
          spends one of those before they have had a chance to look. The design
          asks for Confirm and Confirm is also the safer control.
        */}
        <CodeBoxes value={code} onChange={setCode} disabled={busy} />

        <p className="text-sm text-muted-foreground">
          Sent on WhatsApp to{' '}
          <strong className="font-medium text-foreground">
            {state.pendingPhone ?? 'your number'}
          </strong>
          .{' '}
          {/*
            The same act as Back, said where the number is. A claimant reading
            the number they were sent to is the moment they notice it is wrong,
            and the fix should be under their thumb rather than at the foot of
            the screen — but it goes to the same place, because there is only
            one way to change it.
          */}
          <button
            type="button"
            className="underline underline-offset-2"
            onClick={() => setGoingBack(true)}
          >
            Wrong number?
          </button>
        </p>

        <p className="text-sm text-muted-foreground">
          Did not get it?{' '}
          {remaining > 0 ? (
            <>
              <span className="opacity-60">Send again</span> in 0:
              {String(remaining).padStart(2, '0')}.
            </>
          ) : (
            <button
              type="button"
              disabled={busy}
              className="underline underline-offset-2"
              onClick={() => {
                // A new code means the old refusal no longer describes
                // anything. Left standing, it reads as though the code that has
                // just been sent was already rejected.
                setRemaining(RESEND_SECONDS);
                setAttempted(false);
                setCode('');
                if (state.pendingPhone) void submit({ text: state.pendingPhone });
              }}
            >
              Send again
            </button>
          )}
        </p>

        {/*
          Shown only after a code has actually been sent and refused.

          Still being on this screen is the refusal: a code the server accepted
          moves the whole page on, so anything the bot says while we are still
          here is the reason we are. It was previously gated on the boxes being
          *empty*, which they never are straight after a failed attempt — so the
          message existed and nobody ever saw it.

          Not shown before an attempt: the bot's most recent line on arrival is
          "we have sent you a code", and painting that red would greet everybody
          with a failure they have not had.
        */}
        {attempted && !busy && state.lastReply && (
          <p role="alert" className="text-sm text-destructive">
            {state.lastReply}
          </p>
        )}
      </div>
    </PreClaimLayout>
  );
}

function ConsentStage({ state }: { state: FormState }) {
  const { busy, submit } = useSimpleTurn();
  const t = copyFor(state.locale);
  const [choice, setChoice] = useState<'agree' | 'decline' | null>(null);

  return (
    <PreClaimLayout
      eyebrow="Before we start"
      title="How your data is used"
      subtitle="Please read this before you tell us about your claim."
      actions={
        <Button
          disabled={busy || choice === null}
          onClick={() =>
            void submit({
              callbackValue: choice === 'agree' ? '__consent:agree' : '__consent:decline',
            })
          }
        >
          {busy ? t('saving') : t('continue')}
        </Button>
      }
    >
      {/*
        The notice, shown exactly as the server returned it, under its own
        approved title. Consent recorded against wording written here rather
        than the approved version is unprovable later — which is the whole
        reason notices are versioned and immutable, and why the page heading
        above is the form's and the heading below is the notice's.
      */}
      <div className="flex flex-col gap-3 rounded-xl border bg-background p-5">
        <h2 className="text-[15px] font-bold">
          {state.consent?.title ?? 'Personal Data Protection Notice'}
        </h2>
        <p className="whitespace-pre-wrap text-sm leading-relaxed">{state.consent?.body}</p>
      </div>

      {/*
        Two options rather than two buttons. A pair of buttons makes declining
        look like a way out of the page; a choice makes it what it is — an
        answer, given deliberately, that the next screen acts on.
      */}
      <div role="radiogroup" aria-label="Do you agree?" className="flex flex-col gap-2">
        {[
          { value: 'agree' as const, label: t('agree') },
          { value: 'decline' as const, label: t('decline') },
        ].map(option => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={choice === option.value}
            disabled={busy}
            onClick={() => setChoice(option.value)}
            className={cn(
              'flex min-h-[52px] items-center gap-3 rounded-xl border px-4 text-left text-[15px]',
              choice === option.value
                ? 'border-primary bg-primary/5 font-semibold text-primary'
                : 'border-input hover:border-primary/40'
            )}
          >
            <span
              className={cn(
                'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2',
                choice === option.value
                  ? 'border-primary bg-primary text-[10px] text-primary-foreground'
                  : 'border-muted-foreground'
              )}
            >
              {choice === option.value && <CheckIcon className="h-3 w-3" />}
            </span>
            {option.label}
          </button>
        ))}
      </div>

      <p className="text-xs text-muted-foreground">{t('declineNote')}</p>
    </PreClaimLayout>
  );
}

/**
 * Which kind of claim, and the first screen that shows the section list.
 *
 * Start, Code and Consent have no list: no claim exists, so there is nothing to
 * be part-way through. From here there is — the six sections are a fixed set
 * whose names do not depend on which flow gets chosen, so the list can be shown
 * before the flow is. Seeing the shape of what is ahead is most useful at the
 * point somebody is deciding whether to begin.
 *
 * No case exists yet, so completeness is not read from answers: Claim type is
 * the one in progress and everything after it is untouched.
 */
function ClaimTypeStage({ state }: { state: FormState }) {
  const { busy, submit } = useSimpleTurn();
  const [chosen, setChosen] = useState<string | null>(null);
  const t = copyFor(state.locale);

  const sections: ResolvedSection[] = SECTIONS.map(section => ({
    ...section,
    steps: [],
    complete: false,
    untouched: true,
  }));

  return (
    <SectionLayout
      sections={sections}
      activeId="claim-type"
      title={CLAIM_TYPE_SECTION.heading}
      subtitle={CLAIM_TYPE_SECTION.subtitle}
      locale={state.locale}
      // Nothing has been answered, so the rail has nothing to list. It is still
      // drawn, because a column that appears once the first answer lands would
      // shift the whole page sideways at the worst moment.
      summary={[]}
      /*
        Chosen, then confirmed — rather than advancing on the tap itself.

        This is the one answer that cannot be changed afterwards: it pins the
        flow to the case and decides every question that follows, and there is
        no turn that re-pins it, so a mis-tap costs the whole claim request and
        Start again is the only way back. On a phone, where these are stacked
        and thumb-sized, that is a real risk rather than a theoretical one.
      */
      actions={
        <Button
          disabled={busy || !chosen}
          onClick={() => chosen && void submit({ callbackValue: chosen })}
        >
          {busy ? t('saving') : t('continue')}
        </Button>
      }
    >
      <div className="grid gap-2.5 sm:grid-cols-2">
        {(state.claimTypes ?? []).map(choice => (
          <button
            key={choice.value}
            type="button"
            disabled={busy}
            aria-pressed={chosen === choice.value}
            onClick={() => setChosen(choice.value)}
            className={cn(
              'flex min-h-[56px] items-center gap-3 rounded-xl border px-4 py-3 text-left',
              chosen === choice.value
                ? 'border-primary bg-primary/5'
                : 'border-input bg-background hover:border-primary/40',
              busy && 'opacity-60'
            )}
          >
            {/*
              A filled circle, like the consent screen's. These are five
              options where exactly one is picked and then confirmed, so they
              have to look like a choice being held rather than a button that
              did not fire — which is what a bare tile reads as once the tap no
              longer moves the page.
            */}
            <span
              className={cn(
                'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2',
                chosen === choice.value
                  ? 'border-primary bg-primary text-[10px] text-primary-foreground'
                  : 'border-muted-foreground'
              )}
            >
              {chosen === choice.value && <CheckIcon className="h-3 w-3" />}
            </span>
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="text-sm font-medium">{choice.label}</span>
              {/*
              What the type covers, from the flow rather than written here, so
              the same help appears wherever this question is asked.
            */}
              {choice.description && (
                <span className="text-xs text-muted-foreground">{choice.description}</span>
              )}
            </span>
          </button>
        ))}
      </div>
      {/*
        Not shown. The bot's message here is the question itself — "What has
        happened? Choose the option that fits best" — which the heading above
        already asks. On the chat it is the only place the question can appear;
        on a form it is the same sentence twice.
      */}
    </SectionLayout>
  );
}

/**
 * What happens next, in the order it happens.
 *
 * Three numbered stages rather than three sentences, because the question this
 * page answers is "how long, and what will you do?" — and a list a claimant can
 * count is the difference between waiting and wondering. Each says roughly when
 * and what would interrupt it.
 *
 * Two departures from the approved design, both because the design predates
 * decisions taken since. It said "come back to this site with the same number":
 * progress is per-device now (D1), so that would be a promise the system does
 * not keep. And it offered "Message our team" — the form is submit-only and has
 * no thread for a reply to arrive in, so pointing at one would be a door that
 * opens onto nothing.
 */
function SubmittedStage({ state }: { state: FormState }) {
  const t = copyFor(state.locale);
  const answers = (state.case?.answers ?? {}) as Record<string, unknown>;
  const bank = state.flow?.steps
    .find(step => step.id === 'bank-name')
    ?.choices?.find(choice => choice.value === answers['bank-name'])?.label;

  const stages: Array<[string, string]> = [
    ['Documents checked', 'Usually within one working day. We contact you if anything is unclear.'],
    [
      'Assessment',
      'An adjuster reviews the claim. Some claims need a short video call, which we book with you.',
    ],
    [
      'Decision and payout',
      bank
        ? `Paid to ${bank}. You are told the outcome and the reasons either way.`
        : 'You are told the outcome and the reasons either way.',
    ],
  ];

  return (
    <PreClaimLayout
      title={t('submittedTitle')}
      /*
        A tick above the heading, as the design has it. This is the only page
        in the form that reports an outcome rather than asking for something,
        and after sixteen questions the first thing a claimant wants is to know
        it worked — before reading a word of it.
      */
      icon={
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
          <CheckIcon className="h-7 w-7" />
        </span>
      }
      centred
      /*
        The reference sits with the heading rather than below the fold of it,
        because on this page the reference *is* the outcome — it is the only
        thing a claimant is asked to keep.
      */
      subtitle={
        <>
          Reference{' '}
          <strong className="font-semibold text-foreground">{state.case?.caseNumber}</strong>. Keep
          it. It is how we find your claim request.
        </>
      }
    >
      {/*
        Three cards with air between them, not three rows of one card.

        They are three separate things that happen on three separate days, and
        the design draws them apart for that reason: a divided list reads as one
        block to get through, where separated cards read as steps to count off.
        The numeral is filled rather than tinted for the same reason — it is a
        position in a sequence, not a decorative bullet.
      */}
      <ol className="flex flex-col gap-2.5">
        {stages.map(([title, detail], index) => (
          <li key={title} className="flex gap-3.5 rounded-xl border bg-background p-4">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
              {index + 1}
            </span>
            <div className="flex flex-col gap-1">
              <span className="text-sm font-semibold">{title}</span>
              <span className="text-sm leading-relaxed text-muted-foreground">{detail}</span>
            </div>
          </li>
        ))}
      </ol>

      <p className="text-center text-sm leading-relaxed text-muted-foreground">
        Anything to add or change? Our team will contact you on the number you verified. This page
        will not update.
      </p>

      {/*
        A second claim, which until now there was no way to make.

        This page is where the form ends, and it ended for good: the browser
        holds a session pointing at the submitted claim, so returning to /form
        showed this screen again for ever. Somebody whose luggage was lost on
        the way home from the trip they had just claimed a delay on had nowhere
        to go.

        It is a *fresh start*, not a second claim on this thread — the same rule
        the rest of the form follows. The number is verified again, which is the
        honest cost of it and is said on the button's own line rather than
        discovered afterwards. The submitted claim is untouched: it lives on the
        server under its reference, and only this browser's pointer to it goes.
      */}
      <StartAnother caseNumber={state.case?.caseNumber} />
    </PreClaimLayout>
  );
}

/**
 * Begin a new claim request after one has been submitted.
 *
 * Guarded by the same `isFormChannelSession()` check as `StartAgain`, and for
 * the same reason: inside the Telegram Mini App the session names a messaging
 * binding this page does not own, and clearing it would strand a conversation
 * that lives somewhere else.
 */
function StartAnother({ caseNumber }: { caseNumber?: string | null }) {
  if (isFormChannelSession()) return null;

  return (
    <div className="flex flex-col items-center gap-1.5 border-t pt-5 text-center">
      <Button
        variant="outline"
        onClick={() => {
          clearFormSession();
          window.location.reload();
        }}
      >
        Make another claim request
      </Button>
      <span className="text-xs text-muted-foreground">
        Starts a new request. We will send a code to your number again.
        {caseNumber ? ` Claim ${caseNumber} is not affected.` : ''}
      </span>
    </div>
  );
}

/** The six sections. Everything from here is driven by the flow. */
function FlowStage({ state }: { state: FormState }) {
  useStrayDropGuard();
  const send = useSendFormTurn();
  const refresh = useRefreshFormState();

  const t = copyFor(state.locale);
  const answers = (state.case?.answers ?? {}) as CaseAnswers;
  const claimTypeLabel = state.flow ? TRAVEL_CLAIM_TYPE_LABELS[state.flow.travelClaimType] : null;
  const view = useMemo(
    () => (state.flow ? sectionsFor(state.flow, answers) : null),
    [state.flow, answers]
  );

  const [activeId, setActiveId] = useState<string | null>(null);

  /**
   * The transport the engine sends through. One object, shared by Continue,
   * Change and Submit, so all three pace identically and read a refusal the
   * same way — three copies would drift, and the drift would show up as one
   * button handling a rate limit and another blaming a field.
   */
  const deps = {
    send: async (turn: Parameters<typeof send.mutateAsync>[0]) => {
      const conversation = (await send.mutateAsync(turn)) as {
        currentStep?: { id: string } | null;
        messages?: Array<{ direction: string; text: string | null }>;
      };
      const outbound = (conversation.messages ?? []).filter(m => m.direction === 'OUTBOUND');
      const outcome: TurnOutcome = {
        currentStepId: conversation.currentStep?.id ?? null,
        lastReply: outbound.length ? (outbound[outbound.length - 1].text ?? null) : null,
        /*
          A refusal arrives as two messages: the reason, then the question
          again. The form already shows the question — so what belongs under
          the field is the other one.

          A turn returns the *whole* transcript rather than only what it
          produced, so "the first message" is the greeting from ten minutes
          ago. The re-ask is recognisable instead: the conversation numbers its
          questions, `(4 of 16)`, and nothing else it says is numbered. So the
          reason is the most recent thing said that is not a question being put
          again.
        */
        reason:
          [...outbound]
            .reverse()
            .map(message => message.text ?? '')
            .find(text => text && !NUMBERED_QUESTION.test(text)) ?? null,
      };
      return outcome;
    },
    newId: newTurnId,
    wait: (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms)),
    // The edge throttle answers 429 and axios throws it. Recognised here rather
    // than in the engine, which has no business knowing about HTTP.
    isRateLimitError: isRateLimited,
  };
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  /*
    Punish late, forgive early.

    A field is only ever marked after Continue was pressed, so the message is
    news at the moment it appears. It stops being news the moment the claimant
    acts on it: leaving "Please fill this in before continuing." in red under a
    box they have just filled reads as "still wrong", and the obvious response
    to that is to clear the box and try something else. Cleared on change
    rather than on blur because a phone keyboard can hide the message, and the
    field may never be blurred before Continue is pressed again.
  */
  const clearError = (stepId: string) =>
    setErrors(current => {
      if (!current[stepId]) return current;
      const { [stepId]: _cleared, ...rest } = current;
      return rest;
    });

  // Where a returning claimant lands: the first section that is not finished,
  // or Review once nothing is outstanding.
  //
  // `firstIncomplete` deliberately never names Review — it is finished by
  // submitting, not by answering — so without the last fallback a claimant who
  // completed every section would meet a blank page instead of the thing they
  // came to do.
  const active: ResolvedSection | null =
    view?.sections.find(section => section.id === activeId) ??
    view?.firstIncomplete ??
    view?.sections.find(section => section.id === 'review') ??
    null;

  if (!view || !active || !state.case) return null;

  // Nearest earlier section that has fields — not simply the one before this.
  // `claim-type` holds no steps (the server asks it before a flow exists), so
  // stepping back by one index landed on an empty card. See backTargetFor.
  const previous = backTargetFor(view.sections, active.id);
  const activeContext = {
    currentStepId: state.case.currentStepId,
    values,
    answers,
    steps: active.steps,
    documents: state.case.documents,
  };
  const hasMissingRequired = missingRequired(activeContext).length > 0;

  // Leads with the two facts that are true before any question is answered —
  // the number they proved and the kind of claim they chose. Without them the
  // rail is empty on the first section a claimant sees, which reads as broken
  // rather than as "nothing yet".
  const summary: Array<[string, string]> = [
    ...(claimTypeLabel ? ([['Type of claim', claimTypeLabel]] as Array<[string, string]>) : []),
    ...view.sections
      .flatMap(section => section.steps)
      .filter(step => answers[step.id] !== undefined && step.answerType !== 'confirm')
      .map(step => [step.label, displayAnswer(step, answers[step.id])] as [string, string]),
  ];

  /**
   * Store a file against a step, and report the id a turn can name it by.
   *
   * Shared by the section screens and the review screen rather than written
   * twice: the review screen had no uploader at all, so Replace opened the
   * picker, took a file and did nothing with it — `DocumentField` drops the
   * file when no `onUpload` is passed, and it drops it in silence. The
   * claimant saw the old filename still on the row and tried again.
   *
   * The re-read is part of the upload, not an afterthought: every row that
   * names a document reads the *case* for its filename, because that is the
   * copy that survives a reload. The server retires the document previously
   * attached to this step, so after the refresh there is exactly one.
   */
  const uploadFor = async (step: FlowStep, file: File): Promise<string> => {
    const stored = await uploadFormDocument(file, step.documentType ?? 'OTHER_DOCUMENT', step.id);
    await refresh();
    return stored.id;
  };

  /**
   * Take back one photo from a multi-photo step — `uploadFor`'s counterpart.
   * Shared with the review screen for the same reason `uploadFor` is.
   */
  const removeFor = async (documentId: string): Promise<void> => {
    await removeFormDocument(documentId);
    await refresh();
  };

  /**
   * One answer, moved to and sent — the Change link's whole job.
   *
   * Reports whether the server took it, so the caller can leave the editor
   * open on a refusal. Closing it puts the claimant back on a row showing the
   * value that was just rejected, with no sign anything went wrong.
   */
  const changeOne = async (step: FlowStep, value: string): Promise<boolean> => {
    setBusy(true);
    setErrors({});
    try {
      const result = await submitSection(
        {
          currentStepId: state.case!.currentStepId,
          values: { [step.id]: value },
          answers,
          steps: [step],
        },
        deps
      );
      if (!result.ok && result.error) {
        setErrors({ [result.error.stepId]: result.error.message });
        return false;
      }
      await refresh();
      return true;
    } finally {
      setBusy(false);
    }
  };

  /**
   * Submit. The review step is a `confirm`, and 'true' is what the server
   * reads as "yes, send it" — the same value the chat sends when a claimant
   * taps the button at the end of the thread.
   */
  const onSubmit = async () => {
    const reviewStep = view.sections.find(section => section.id === 'review')?.steps[0];
    if (!reviewStep) return;

    setBusy(true);
    setErrors({});
    try {
      // Sent directly rather than through `submitSection`, because the engine's
      // test for "was that accepted?" is "did the cursor move off this step" —
      // and there is nowhere for it to move to. This is the last question. The
      // honest test is the stage the server reports afterwards, so the reply is
      // read for a refusal and the rest is left to `/state`.
      const outcome = await deps.send({
        clientMessageId: newTurnId(),
        callbackValue: 'true',
        callbackStepId: reviewStep.id,
        locale: browserLocale(),
      });

      await refresh();

      // Still on the review step with something to say means it was refused —
      // a missing required document, most often.
      if (outcome.currentStepId === reviewStep.id && outcome.lastReply) {
        setErrors({ __submit: outcome.lastReply });
      }
    } finally {
      setBusy(false);
    }
  };

  /**
   * A document step's row, as filenames rather than a count.
   *
   * `find`ing the first match was correct only because every document step
   * used to hold at most one live file. A multi-photo step (`allowMultiple`)
   * can hold several, and a bare count ("3 photos") would undo the reason
   * this shows filenames at all: at the point of submitting, "did I attach
   * the right ones?" is the question, and neither "provided" nor a count
   * answers it.
   */
  const documentAnswerFor = (
    step: FlowStep,
    documents: Array<{ fileName: string; stepId: string | null }>
  ): string => {
    const attached = documents.filter(document => document.stepId === step.id);
    if (attached.length === 0) return 'Provided';
    // One filename per line — `ReviewStage`'s row carries `whitespace-pre-line`
    // for exactly this, since a comma-run of names is hard to scan when one
    // step holds several.
    return attached.map(document => document.fileName).join('\n');
  };

  /**
   * How each answer reads on the review page.
   *
   * Documents show their filename rather than "provided": at the point of
   * submitting, "did I attach the right one?" is the question being asked, and
   * "provided" does not answer it.
   */
  const reviewRowsFor = (section: ResolvedSection): ReviewRow[] => {
    // The claim type is a real answer with no step in `flow.steps` — the server
    // asked it before a flow existed. Without this the review page opens on
    // "You & your trip" and never shows the one choice that decided everything
    // below it, which is exactly the answer somebody is most likely to want to
    // change.
    if (section.id === 'claim-type') {
      return claimTypeLabel
        ? [
            {
              step: { id: '__claim-type', label: 'Type of claim', answerType: 'text' } as FlowStep,
              label: 'Type of claim',
              value: claimTypeLabel,
              /*
                Shown, but not editable — the one place this page departs from
                the design. The claim type chose the flow, and the flow is
                pinned to the case: changing it would invalidate every answer
                below, and the server has no turn that does it. A Change link
                that cannot change anything is worse than none, so somebody who
                picked the wrong type takes Start again, which is honest about
                what it costs.
              */
              editable: false,
            },
          ]
        : [];
    }

    return section.steps
      .filter(step => step.answerType !== 'confirm')
      .filter(step => answers[step.id] !== undefined)
      .map(step => ({
        step,
        label: step.label,
        value:
          step.answerType === 'document'
            ? documentAnswerFor(step, state.case!.documents)
            : displayAnswer(step, answers[step.id]),
      }));
  };

  const onContinue = async () => {
    setBusy(true);
    setErrors({});
    try {
      const context = {
        currentStepId: state.case!.currentStepId,
        values,
        answers,
        // Confirm steps included: a notice in the section is acknowledged by
        // continuing past it, the way tapping Confirm acknowledges it in a
        // thread. `missingRequired` still ignores them — there is nothing in
        // one to fill in.
        steps: active.steps,
        documents: state.case!.documents,
      };

      /*
        Say what is missing before sending anything.

        An empty required field produces no turn — it is not a changed answer
        and only optional questions are skipped — so the server never sees it
        and never objects. Pressing Continue on an untouched section therefore
        did nothing whatsoever: no movement, no message, nothing to act on.

        Checked here rather than left to the server because the server can only
        ever object to one question at a time, and a claimant who has filled in
        none of six would be walked through them one refusal at a time.
      */
      const missing = missingRequired(context);
      if (missing.length > 0) {
        setErrors(
          Object.fromEntries(
            missing.map(step => [
              step.id,
              step.answerType === 'document'
                ? 'Please add this document before continuing.'
                : 'Please fill this in before continuing.',
            ])
          )
        );
        focusField(missing[0].id);
        return;
      }

      const result = await submitSection(context, deps);

      if (!result.ok && result.error) {
        setErrors({ [result.error.stepId]: result.error.message });
        focusField(result.error.stepId);
        return;
      }

      // Forget only what was just sent — not everything that has been typed.
      //
      // These answers are the server's now and come back through `answers`, so
      // holding a second copy here would let the two disagree. That was the
      // whole reason for clearing. But clearing the *entire* map also threw
      // away sections nobody had submitted yet, and Back is the only way out of
      // a section while Continue is the only way forward — so filling in half
      // of Payout, going Back to check something, and continuing forward again
      // returned to Payout with the fields silently blank. Nothing warned,
      // because nothing knew it had been discarded.
      const justSent = new Set(active.steps.map(step => step.id));
      setValues(current =>
        Object.fromEntries(Object.entries(current).filter(([stepId]) => !justSent.has(stepId)))
      );
      // Re-read rather than reasoning about what changed. A branch may have
      // opened or closed as a result of what was just answered, and the server
      // already knows which.
      setActiveId(null);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionLayout
      sections={view.sections}
      activeId={active.id}
      title={
        active.id === 'evidence'
          ? `Evidence for a ${claimTypeLabel?.toLowerCase() ?? 'claim'}`
          : active.heading
      }
      /*
        The claim type as a chip beside the line rather than as the subtitle,
        matching the design: it labels what follows instead of describing it,
        and five screens in it answers "which claim is this again?" without
        being read as an instruction.
      */
      subtitle={
        active.id === 'what-happened' ? (
          <span className="flex flex-wrap items-center gap-2">
            {claimTypeLabel && (
              <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
                {claimTypeLabel}
              </span>
            )}
            <span>{active.subtitle}</span>
          </span>
        ) : (
          active.subtitle
        )
      }
      summary={summary}
      locale={state.locale}
      actions={
        active.id === 'review' ? null : (
          <>
            {/*
              Back moves between *sections* of the form, not through the
              server's cursor, so it is navigation rather than an undo.

              Answers already submitted are on the server. Anything typed but
              not yet submitted stays in `values` — onContinue drops only the
              section it just sent — so a half-filled section survives leaving
              it and coming back. It does not survive a reload, because nothing
              in it has been sent yet; that is why this warns about neither.
            */}
            {previous && (
              <Button variant="outline" disabled={busy} onClick={() => setActiveId(previous.id)}>
                {t('back')}
              </Button>
            )}
            <Button disabled={busy || hasMissingRequired} onClick={() => void onContinue()}>
              {busy ? t('saving') : t('continue')}
            </Button>
          </>
        )
      }
    >
      {active.id === 'review' ? (
        <ReviewStage
          sections={view.sections}
          answers={answers}
          documents={state.case.documents}
          rowsFor={reviewRowsFor}
          busy={busy}
          error={Object.values(errors)[0] ?? null}
          onChange={changeOne}
          onUpload={uploadFor}
          onRemove={removeFor}
          onSubmit={onSubmit}
          onBack={() => previous && setActiveId(previous.id)}
          locale={state.locale}
        />
      ) : (
        <div className="flex flex-col gap-5 rounded-xl border bg-background p-5">
          {rowsFor(active.steps).map(row => (
            <div key={row.map(step => step.id).join('+')} className={rowClassFor(row)}>
              {row.map(step => (
                <FieldControl
                  key={step.id}
                  step={step}
                  disabled={busy}
                  error={errors[step.id]}
                  value={values[step.id] ?? String(answers[step.id] ?? '')}
                  onChange={value => {
                    setValues(current => ({ ...current, [step.id]: value }));
                    clearError(step.id);
                  }}
                  attached={state.case!.documents.filter(document => document.stepId === step.id)}
                  onRemove={removeFor}
                  onUpload={async file => {
                    const storedId = await uploadFor(step, file);
                    setValues(current => ({ ...current, [step.id]: storedId }));
                    clearError(step.id);
                  }}
                />
              ))}
            </div>
          ))}

          {/*
          Said on the screen where documents are actually handed over, not only
          in the footer. MASTER_PLAN §6 is explicit that AI is disclosed rather
          than downplayed, and "an extractor reads this" is a different, more
          concrete claim than "parts of the assessment use AI" — this is the
          moment a claimant can decide whether they are comfortable with it.
        */}
          {active.id === 'evidence' && (
            <>
              {/*
              Two hints, one per device, because they describe two different
              gestures. Telling a phone user to drag a file is an instruction
              they cannot follow, and it is the screen where somebody most
              needs to know what the button is about to open.
            */}
              {/*
              "Onto the row", not "here". A drop is only unambiguous on the row
              it lands on: a file dropped on the card would have to be guessed
              into one of three document types, and a boarding pass filed as an
              airline delay letter is a wrong answer nobody sees until an
              adjuster opens it.
            */}
              <p className="hidden text-xs text-muted-foreground sm:block">
                Or drag a file onto the row it belongs to. Use JPG, PNG, HEIC or PDF, up to 50 MB.
              </p>
              <p className="text-xs text-muted-foreground sm:hidden">
                On a phone, “Add” opens your camera or photo library.
              </p>
              <p className="rounded-lg bg-muted/60 p-3 text-xs leading-relaxed text-muted-foreground">
                Documents are read by an automated extractor to pre-fill your claim. An adjuster
                checks the result.
              </p>
            </>
          )}

          {/*
          The commonest reason a payout is delayed, said where the account is
          entered rather than discovered weeks later.

          It repeats the flow's own hint on `bank-account-holder`, which I had
          removed for that reason and have put back: it is in the design, and a
          claimant skimming a form reads a boxed warning where they do not read
          a grey line under a field. If the server's wording changes, this is
          the copy that will go stale — it is the only place on this surface
          that restates something the flow already says.
        */}
          {active.id === 'payout' && (
            <p className="rounded-lg border border-amber-300 bg-amber-50 p-3.5 text-xs leading-relaxed text-amber-900">
              If the account is in someone else&rsquo;s name, give their name here. We will ask
              about it later. A name mismatch is the most common reason a payout is delayed.
            </p>
          )}
        </div>
      )}

      <StartAgain />
    </SectionLayout>
  );
}

/**
 * Start over.
 *
 * Its own copy rather than a shared hook, deliberately: the chat's version is
 * guarded by the same `isChannelSession()` check and asserted by a source scan,
 * and sharing one would make that assertion pass for a page that no longer had
 * the guard. Two copies of four lines, each provably guarded, beats one
 * abstraction that has to be trusted.
 *
 * Clears the **form's** key only. The chat's conversation in the same browser
 * is a different channel and is left alone.
 */
function StartAgain() {
  if (isFormChannelSession()) return null;

  return (
    <button
      type="button"
      className="self-start text-xs text-muted-foreground underline"
      onClick={() => {
        if (
          !window.confirm(
            'This clears the claim request you are filling in on this device. The web chat is not affected.'
          )
        )
          return;
        clearFormSession();
        window.location.reload();
      }}
    >
      Start again
    </button>
  );
}

function displayAnswer(step: FlowStep, value: unknown): string {
  // "skip" and "later" are how the engine closes a question nobody answered.
  // They are the right thing to send and the wrong thing to show: a summary
  // reading "Policy number — skip" looks like somebody typed the word.
  const raw = String(value ?? '')
    .trim()
    .toLowerCase();
  if (raw === 'skip') return 'Not provided';
  if (raw === 'later') return 'To follow';

  if (step.answerType === 'document') return 'provided';
  if (step.answerType === 'choice') {
    return step.choices?.find(choice => choice.value === value)?.label ?? String(value);
  }
  if (step.answerType === 'date' || step.answerType === 'datetime') {
    return formatDateAnswer(String(value), step.answerType) ?? String(value);
  }
  return String(value);
}
